/**
 * mapCodec.js — Binary encode/decode for map layers
 *
 * Terrain: 1 byte per cell (index into TERRAIN_TYPES)
 * Fog:     1 bit per cell (bit-packed booleans)
 * Elevation: 1 byte per cell (0–255)
 */

import { TERRAIN_TYPES } from './terrain';

// Build index lookup once
const TERRAIN_ID_TO_IDX = Object.fromEntries(TERRAIN_TYPES.map((t, i) => [t.id, i]));

// ── Terrain ──────────────────────────────────────────────────────────────────

export function encodeTerrain(terrainArray) {
  const bytes = new Uint8Array(terrainArray.length);
  for (let i = 0; i < terrainArray.length; i++) {
    bytes[i] = TERRAIN_ID_TO_IDX[terrainArray[i]] ?? 0;
  }
  return uint8ToBase64(bytes);
}

export function decodeTerrain(base64, length) {
  const bytes = base64ToUint8(base64);
  const result = new Array(length);
  for (let i = 0; i < length; i++) {
    const idx = i < bytes.length ? bytes[i] : 0;
    result[i] = idx < TERRAIN_TYPES.length ? TERRAIN_TYPES[idx].id : 'empty';
  }
  return result;
}

// ── Fog (bit-packed) ─────────────────────────────────────────────────────────

export function encodeFog(fogArray) {
  const byteLen = Math.ceil(fogArray.length / 8);
  const bytes = new Uint8Array(byteLen);
  for (let i = 0; i < fogArray.length; i++) {
    if (fogArray[i]) bytes[i >> 3] |= (1 << (i & 7));
  }
  return uint8ToBase64(bytes);
}

export function decodeFog(base64, length) {
  const bytes = base64ToUint8(base64);
  const result = new Array(length);
  for (let i = 0; i < length; i++) {
    result[i] = i < bytes.length * 8 ? !!(bytes[i >> 3] & (1 << (i & 7))) : false;
  }
  return result;
}

// ── Elevation ────────────────────────────────────────────────────────────────

export function encodeElevation(elevArray) {
  const bytes = new Uint8Array(elevArray.length);
  for (let i = 0; i < elevArray.length; i++) {
    bytes[i] = Math.max(0, Math.min(255, elevArray[i] || 0));
  }
  return uint8ToBase64(bytes);
}

export function decodeElevation(base64, length) {
  const bytes = base64ToUint8(base64);
  const result = new Array(length);
  for (let i = 0; i < length; i++) {
    result[i] = i < bytes.length ? bytes[i] : 0;
  }
  return result;
}

// ── Base64 helpers ───────────────────────────────────────────────────────────

function uint8ToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function base64ToUint8(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
